// =============================================================================
// FEEDBACK MODAL - Модальне вікно для відгуків (Tactical HUD Style)
// =============================================================================

import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  submitFeedback,
  markFeedbackSubmitted,
  type FeedbackData,
} from '../services/feedbackService';
import { trackFeedback } from '../services/analytics';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  gameResult: 'victory' | 'stalemate' | 'defeat' | 'gameOver';
  averageScore?: number;
  decisionNumber?: number;
}

const STAR_LABELS = ['Жахливо', 'Погано', 'Нормально', 'Добре', 'Чудово!'];

export function FeedbackModal({
  isOpen,
  onClose,
  gameResult,
  averageScore,
  decisionNumber,
}: FeedbackModalProps) {
  const [rating, setRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(async () => {
    if (rating === 0) {
      setError('Будь ласка, оберіть оцінку');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const feedback: FeedbackData = {
      rating,
      message: message.trim(),
      email: email.trim() || undefined,
      gameResult,
      averageScore,
      decisionNumber,
      timestamp: new Date().toISOString(),
    };

    try {
      await submitFeedback(feedback);
      markFeedbackSubmitted();
      trackFeedback(rating, gameResult, message.length > 0);
      setIsSubmitted(true);

      // Закрити через 2 секунди після успішної відправки
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch {
      setError('Помилка відправки. Спробуйте ще раз.');
    } finally {
      setIsSubmitting(false);
    }
  }, [rating, message, email, gameResult, averageScore, decisionNumber, onClose]);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onClose();
    }
  }, [isSubmitting, onClose]);

  const displayRating = hoveredRating || rating;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="w-full max-w-md bg-war-panel border-2 border-war-dim/40 relative overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Corner Accents */}
            <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-war-primary" />
            <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-war-primary" />
            <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-war-primary" />
            <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-war-primary" />

            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-war-dim/20 bg-war-bg/50">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-war-primary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-war-bright font-mono text-sm font-bold uppercase tracking-wider">
                    Зворотний зв'язок
                  </h2>
                  <p className="text-war-dim text-[10px] font-mono uppercase">
                    FEEDBACK_TERMINAL
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                disabled={isSubmitting}
                className="w-8 h-8 flex items-center justify-center text-war-dim hover:text-war-red transition-colors disabled:opacity-50"
                aria-label="Закрити"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center py-8"
                >
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-war-primary/20 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-war-primary"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <h3 className="text-war-bright font-mono text-lg font-bold mb-2">
                    Дякуємо за відгук!
                  </h3>
                  <p className="text-war-dim font-mono text-sm">
                    Ваша думка допоможе нам покращити гру
                  </p>
                </motion.div>
              ) : (
                <>
                  {/* Rating Stars */}
                  <div className="space-y-3">
                    <label className="block text-war-dim text-[10px] uppercase font-mono tracking-wider">
                      Оцініть ваш досвід:
                    </label>
                    <div className="flex items-center justify-center gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setRating(star)}
                          onMouseEnter={() => setHoveredRating(star)}
                          onMouseLeave={() => setHoveredRating(0)}
                          className="p-1 transition-transform hover:scale-110 active:scale-95 focus:outline-none focus:ring-2 focus:ring-war-primary focus:ring-offset-2 focus:ring-offset-war-panel rounded"
                          aria-label={`Оцінка ${star} з 5: ${STAR_LABELS[star - 1]}`}
                        >
                          <svg
                            className={`w-10 h-10 transition-colors ${
                              star <= displayRating
                                ? 'text-war-amber fill-war-amber'
                                : 'text-war-dim/30'
                            }`}
                            fill={
                              star <= displayRating ? 'currentColor' : 'none'
                            }
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                            />
                          </svg>
                        </button>
                      ))}
                    </div>
                    {displayRating > 0 && (
                      <p className="text-center text-war-primary font-mono text-sm font-bold">
                        {STAR_LABELS[displayRating - 1]}
                      </p>
                    )}
                  </div>

                  {/* Message Textarea */}
                  <div className="space-y-3">
                    <label
                      htmlFor="feedback-message"
                      className="block text-war-dim text-[10px] uppercase font-mono tracking-wider"
                    >
                      Ваш відгук (необов'язково):
                    </label>
                    <textarea
                      id="feedback-message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Опишіть ваші враження, пропозиції або знайдені проблеми..."
                      rows={4}
                      maxLength={1000}
                      className="w-full bg-war-bg border border-war-dim/30 text-war-bright font-mono text-sm p-3 placeholder:text-war-dim/50 focus:outline-none focus:border-war-primary resize-none"
                    />
                    <p className="text-right text-war-dim text-[10px] font-mono">
                      {message.length} / 1000
                    </p>
                  </div>

                  {/* Email Input (Optional) */}
                  <div className="space-y-3">
                    <label
                      htmlFor="feedback-email"
                      className="block text-war-dim text-[10px] uppercase font-mono tracking-wider"
                    >
                      Email для зв'язку (необов'язково):
                    </label>
                    <input
                      id="feedback-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full bg-war-bg border border-war-dim/30 text-war-bright font-mono text-sm p-3 placeholder:text-war-dim/50 focus:outline-none focus:border-war-primary"
                    />
                    <p className="text-war-dim text-[10px] font-mono">
                      Залиште email, якщо хочете отримати відповідь
                    </p>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-war-red font-mono text-sm text-center"
                    >
                      {error}
                    </motion.p>
                  )}

                  {/* Submit Button */}
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting || rating === 0}
                    className="w-full py-4 bg-war-primary text-war-bg font-mono font-bold uppercase tracking-widest text-sm transition-all hover:bg-war-bright disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <svg
                          className="w-4 h-4 animate-spin"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Відправка...
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                          />
                        </svg>
                        Надіслати відгук
                      </>
                    )}
                  </button>

                  {/* Skip Link */}
                  <button
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className="w-full py-2 text-war-dim font-mono text-xs uppercase tracking-wider hover:text-war-bright transition-colors disabled:opacity-50"
                  >
                    Пропустити
                  </button>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-war-dim/10 bg-war-bg/30">
              <div className="flex items-center justify-center gap-2 text-[9px] text-war-dim/50 font-mono tracking-wider uppercase">
                <span>Канали зв'язку:</span>
                <div className="flex items-center gap-1.5">
                  <a
                    href="https://t.me/zavoruha_tech"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-war-primary transition-colors"
                  >
                    [TG]
                  </a>
                  <a
                    href="https://www.instagram.com/zavoruha"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-war-primary transition-colors"
                  >
                    [IG]
                  </a>
                  <a
                    href="https://www.threads.com/@zavoruha"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-war-primary transition-colors"
                  >
                    [TR]
                  </a>
                  <a
                    href="https://www.facebook.com/profile.php?id=61587632097637"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-war-primary transition-colors"
                  >
                    [FB]
                  </a>
                </div>
              </div>
              <p className="text-center text-war-dim/40 text-[8px] font-mono uppercase mt-1.5">
                Ваші дані захищені та використовуються лише для покращення гри
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
